import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  TableView as ExcelIcon,
} from "@mui/icons-material";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import customFetch from "hooks/fetchWrapper";
import { toast } from "react-toastify";
import moment from "moment";
import StudentDetailsDialog from "./StudentDetailsDialog";

const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  console.log("🚀 ~ StudentManagement ~ students:", students);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  console.log("🚀 ~ StudentManagement ~ selectedStudent:", selectedStudent);
  const [openViewDialog, setOpenViewDialog] = useState(false);

  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRangeFilter, setDateRangeFilter] = useState(null);

  const { afterToday } = DateRangePicker;

  // Configurable columns
  const columns = [
    {
      field: "StudentDetails.Session",
      headerName: "Session",
      width: 100,
      editable: false,
      valueGetter: (params) =>
        params.row.schoolAcademicYear ||
        params.row.Session ||
        params.row.StudentDetails?.Session ||
        "",
    },
    {
      field: "RollNo",
      headerName: "Roll No",
      width: 100,
      editable: false,
      valueGetter: (params) =>
        params.row.admissionNo ||
        params.row.RollNo ||
        params.row.rollNo ||
        "",
    },
    {
      field: "StudentDetails.MemSName",
      headerName: "Student Name",
      width: 200,
      editable: false,
      valueGetter: (params) =>
        params.row.studentName ||
        params.row.StudentName ||
        params.row.MemFName ||
        params.row.MemSName ||
        params.row.StudentDetails?.MemFName ||
        params.row.StudentDetails?.MemSName ||
        params.row.StudentDetails?.StudentName ||
        params.row.StudentDetails?.studentName ||
        "",
    },
    {
      field: "StudentDetails.classname",
      headerName: "Class",
      width: 80,
      editable: false,
      valueGetter: (params) =>
        params.row.grade ||
        params.row.classname ||
        params.row.Class ||
        params.row.StudentDetails?.classname ||
        "",
    },
    {
      field: "StudentDetails.section",
      headerName: "Section",
      width: 80,
      editable: false,
      valueGetter: (params) =>
        params.row.division ||
        params.row.section ||
        params.row.Section ||
        params.row.StudentDetails?.section ||
        "",
    },
    {
      field: "EmiratesName",
      headerName: "Emirates",
      width: 100,
      editable: false,
    },
    {
      field: "RouteName",
      headerName: "Route",
      width: 150,
      editable: false,
    },
    {
      field: "BustopName",
      headerName: "Bus Stop",
      width: 150,
      editable: false,
    },
    {
      field: "ServiceType",
      headerName: "Service Type",
      width: 150,
      valueGetter: (params) => getServiceTypeDescription(Number(params.row?.ServiceType)) || "",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleViewStudent(params.row)}
            startIcon={<ViewIcon />}
          >
            View
          </Button>
          <IconButton color="error" size="small" onClick={() => handlePrintPDF(params.row)}>
            <PdfIcon />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const fetchStudents = async () => {
    try {
      const response = await customFetch("application");
      console.log("🚀 ~ fetchStudents ~ response:", response);
      setStudents(response);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };
  useEffect(() => {
    fetchStudents();
  }, []);

  // Filtering effect
  useEffect(() => {
    let result = students;

    // Status filtering
    if (statusFilter !== "all") {
      result = result.filter((student) => student.Status === statusFilter);
    }

    // Date range filtering
    if (dateRangeFilter && dateRangeFilter[0] && dateRangeFilter[1]) {
      result = result.filter((student) => {
        const studentDate = moment(student.addeddate);
        return studentDate.isBetween(
          moment(dateRangeFilter[0]),
          moment(dateRangeFilter[1]),
          "day",
          "[]"
        );
      });
    }

    setFilteredStudents(result);
  }, [students, statusFilter, dateRangeFilter]);

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedStudent(null);
  };

  const handleEditStudent = async (updatedStudent) => {
    try {
      // Refetch the entire students list to ensure you have the latest data
      await fetchStudents();
      // Close the dialog
      setOpenViewDialog(false);
      setSelectedStudent(null);
    } catch (error) {
      console.error("Error updating student:", error);
    }
  };

  const handleDeleteStudent = () => {
    // Implement delete functionality
    console.log("Delete student:", selectedStudent);
  };

  const API_BASE_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_BASE_API ||
    "http://localhost:8000/api/";

  const handlePrintPDF = async (student) => {
    const loadingToastId = toast.loading("Generating PDF... Please wait");

    try {
      const studentNameVal =
        student.studentName ||
        student.StudentName ||
        student.MemSName ||
        student.MemFName ||
        student.StudentDetails?.MemSName ||
        student.StudentDetails?.MemFName ||
        student.StudentDetails?.StudentName ||
        student.StudentDetails?.studentName ||
        "";

      const sessionVal =
        student.schoolAcademicYear ||
        student.Session ||
        student.StudentDetails?.Session ||
        "";

      const gradeVal =
        student.grade ||
        student.classname ||
        student.StudentDetails?.classname ||
        "";

      const divisionVal =
        student.division ||
        student.section ||
        student.StudentDetails?.section ||
        "";

      const formBody = {
        schoolAcademicYear: sessionVal,
        admissionNo: student.admissionNo || student.RollNo,
        date: student.addeddate ? moment(student.addeddate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        studentName: studentNameVal,
        grade: gradeVal,
        division: divisionVal,
        type: student.ServiceType,
        confirmedDateOfEntry: student.addeddate,
        emirateid: student.EmiratesId,
        routeid: student.RouteId,
        busStopid: student.BusStopId,
        address: student.address,


        oldEmirateid: student.oldEmiratesid || null,
        newEmirateid: student.EmiratesId,
        oldRouteid: student.oldRouteid || null,
        newRouteid: student.RouteId,
        oldBusStopid: student.oldBustopid || null,
        newBusStopid: student.BusStopId,
        oldLocation: student.oldLocation || null,
        newLocation: student.Location || null,
        noticeDate: student.NoticeDate ? moment(student.NoticeDate).format("YYYY-MM-DD") : null,
        cancellationDate: student.CancellationDate
          ? moment(student.CancellationDate).format("YYYY-MM-DD")
          : null,
        cancellationReason: student.CancellationReason || null,
      };
      console.log("🚀 ~ handlePrintPDF ~ formBody:", formBody);

      // Make API call to generate PDF
      const response = await fetch(`${API_BASE_URL}generatepdfonly`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formBody),
      });

      // Check if the response is OK
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to generate PDF");
      }

      // Get the response as a Blob (PDF file)
      const blob = await response.blob();

      // Create a link element to download the PDF
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = url;
      const fileName = studentNameVal
        ? `${studentNameVal.replace(/[^a-zA-Z0-9_-]/g, "_")}_transport_form.pdf`
        : (student.RollNo ? `${student.RollNo}_transport_form.pdf` : "transport_form.pdf");
      link.download = fileName;
      document.body.appendChild(link);
      link.click();


      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Update the toast to show success
      toast.update(loadingToastId, {
        render: "PDF generated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000, // Automatically close after 3 seconds
      });
    } catch (error) {
      console.error("Error generating PDF:", error);

      // Update the toast to show error
      toast.update(loadingToastId, {
        render: error.message || "Failed to generate PDF. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 5000, // Automatically close after 5 seconds
      });
    }
  };

  const getServiceTypeDescription = (serviceType) => {
    switch (serviceType) {
      case 1:
        return "New Request";
      case 2:
        return "Change Request";
      case 3:
        return "Cancel Request";
      default:
        return "Unknown";
    }
  };

  const EXCEL_GEN_URL = process.env.REACT_APP_EXCEL_GEN;

  const handleExportExcel = async () => {
    if (!filteredStudents || filteredStudents.length === 0) {
      return Promise.reject(new Error("No data available to generate Excel."));
    }

    const selectedFields = filteredStudents?.map((student) => ({
      Session: student.StudentDetails?.Session,
      RollNo: student.RollNo,
      StudentName: student.StudentDetails?.MemSName + " " + student.StudentDetails?.MemMName,
      classname: student.StudentDetails?.classname,
      section: student.StudentDetails?.section,
      DateofAdmission: student.addeddate,
      SubmitDate: student.addeddate,
      address: student.address,
      EmiratesName: student.EmiratesName,
      RouteName: student.RouteName,
      BustopName: student.BustopName,
      Location: student.Location,
      oldEmirates: student.oldEmirates,
      oldRoute: student.oldRoute,
      oldBustop: student.oldBustop,
      oldLocation: student.oldLocation,
      FatherTelephone: student.StudentDetails?.FPhoneM || "",
      MotherTelephone: student.StudentDetails?.MPhoneM || "",
      Email: student.StudentDetails?.Email || "",
      ServiceType: getServiceTypeDescription(Number(student.ServiceType)),
    }));

    // Prepare payload
    const payload = {
      filename: `StudentData_${new Date().toISOString()}`, // Current date-time as filename
      exceldata: selectedFields,
    };

    try {
      const response = await fetch(`${EXCEL_GEN_URL}rams-generate-excel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Process the Excel file for download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `${payload.filename}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log("Excel downloaded successfully!");
    } catch (error) {
      console.error("Error generating Excel:", error.message);
      alert("Error generating Excel. Please try again.");
    }
  };

  return (
    <Box sx={{ width: "100%", height: 600 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">Application Manage</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Date Range Picker */}
          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            style={{ width: 230 }}
            onChange={(range) => setDateRangeFilter(range)}
            shouldDisableDate={afterToday()}
          />
          {/* Status Filter */}
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <Button variant="outlined" startIcon={<ExcelIcon />} onClick={handleExportExcel}>
            Export Excel
          </Button>
        </Stack>
      </Box>

      <DataGrid
        rows={filteredStudents}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick
      />

      <StudentDetailsDialog
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          setSelectedStudent(null);
        }}
        student={selectedStudent}
        onSave={handleEditStudent}
      />
    </Box>
  );
};

export default StudentManagement;
